import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { css, Grid, Label, Toast, PageHeader, SectionHeader, MontoInput } from '../components/UI';
import { hoyISO } from '../utils';
import { Icono, ConIcono } from '../components/Iconos';

const BASE = process.env.REACT_APP_API_URL || '/api';
// Tasa de IVA POR PARTIDA (porcentaje), heredada del catálogo de productos y
// editable aquí. Antes era un solo checkbox de 16% para toda la requisición, así
// que una compra mixta —despensa exenta junto a material gravado— pagaba IVA de
// más. Misma regla que backend/src/utils/ivaPartidas.js, que es quien manda.
const TASAS_IVA = [16, 8, 0];
const TASA_IVA_DEFAULT = 16;
const tasaDe = (p) => { const t = Number(p?.tasaIva); return Number.isFinite(t) && t >= 0 ? t : TASA_IVA_DEFAULT; };

const partidaVacia = () => ({ material: '', descripcion: '', unidad: '', cantidad: '', precioUnitario: '', tasaIva: TASA_IVA_DEFAULT });

// Evidencia de la justificación: los mismos topes que valida el backend
const MAX_IMAGENES = 5;
const MAX_IMAGEN_MB = 10;
const IMAGEN_RE = /\.(jpe?g|png|webp)$/i;

// Buscador de materiales del catálogo: filtra en vivo por nombre o código para
// no recorrer toda la lista. Si no eliges nada, la partida queda en captura libre.
function MaterialPicker({ materiales, partida, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // Posición del menú en coordenadas de viewport (position: fixed) para que no
  // lo recorte el contenedor con scroll de la tabla de partidas.
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const abrir = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 3, left: r.left, width: Math.max(r.width, 320) });
    setOpen(true);
  };

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    // Al hacer scroll el menú sigue al campo (posición fija). Antes se cerraba, y en
    // el teléfono escribir desplaza la tabla de partidas: se cerraba al teclear.
    // Solo se cierra si el campo sale de la pantalla.
    const seguir = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) { setOpen(false); return; }
      setPos({ top: r.bottom + 3, left: r.left, width: Math.max(r.width, 320) });
    };
    document.addEventListener('mousedown', h);
    window.addEventListener('scroll', seguir, true);
    window.addEventListener('resize', seguir);
    return () => {
      document.removeEventListener('mousedown', h);
      window.removeEventListener('scroll', seguir, true);
      window.removeEventListener('resize', seguir);
    };
  }, []);

  // Más ancho que la columna (los nombres del catálogo son largos), siempre dentro de la pantalla
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const ancho = Math.min(Math.max(pos.width, 460), vw - 16);
  const izq = Math.max(8, Math.min(pos.left, vw - ancho - 8));

  const texto = q.trim().toLowerCase();
  const filtrados = (texto
    ? materiales.filter(m => m.nombre.toLowerCase().includes(texto) || (m.codigo || '').toLowerCase().includes(texto))
    : materiales
  ).slice(0, 30);

  const sel = materiales.find(m => m._id === partida.material);
  if (sel) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span title={sel.nombre} style={{ flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid var(--ok-line)', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
          <ConIcono nombre="caja" size={13}>{sel.nombre}</ConIcono>
        </span>
        <button onClick={() => { onClear(); setQ(''); }} title="Quitar material (captura libre)"
          style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--glass)', cursor: 'pointer', fontSize: 12 }}>×</button>
      </div>
    );
  }
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input ref={inputRef} value={q} onFocus={abrir}
        onChange={e => { setQ(e.target.value); abrir(); }}
        placeholder="Buscar material... (o captura libre)"
        style={{ ...css.input(false), fontSize: 12 }} />
      {open && (
        <div style={{ position: 'fixed', top: pos.top, left: izq, width: ancho, zIndex: 1000, background: 'var(--panel)', border: '1px solid var(--mesh)', borderRadius: 10, boxShadow: '0 10px 30px rgba(15,23,42,0.18)', maxHeight: 320, overflowY: 'auto' }}>
          {filtrados.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--glass)' }}>
              Sin coincidencias — escribe la descripción directamente en la partida (captura libre).
            </div>
          ) : filtrados.map(m => (
            <div key={m._id} onMouseDown={() => { onPick(m); setOpen(false); setQ(''); }}
              style={{ padding: '8px 12px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--hair-soft)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--plasma-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ color: 'var(--ink)', fontWeight: 500, lineHeight: 1.35, overflowWrap: 'anywhere', minWidth: 0 }}>
                {m.nombre}
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--plasma)', flexShrink: 0, textAlign: 'right' }}>
                {m.grupo || 'PRODUCTO'}{m.unidad ? ` · ${m.unidad}` : ''}
                {(m.costoUnitario !== null && m.costoUnitario !== undefined) && <span style={{ display: 'block', color: 'var(--glass)', fontWeight: 600 }}>${Number(m.costoUnitario).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>}
              </span>
            </div>
          ))}
          {texto && filtrados.length > 0 && materiales.length > filtrados.length && (
            <div style={{ padding: '6px 12px', fontSize: 11.5, color: 'var(--glass)', background: 'var(--panel)' }}>Mostrando los primeros {filtrados.length} — afina la búsqueda para ver más.</div>
          )}
        </div>
      )}
    </div>
  );
}

// Miniatura de una imagen de la justificación. `guardada` distingue las que ya
// están en el servidor (al corregir) de las que aún no se han subido.
function Miniatura({ src, nombre, guardada, onQuitar }) {
  return (
    <div style={{ position: 'relative', width: 108 }}>
      <img src={src} alt={nombre} title={nombre}
        style={{ width: 108, height: 82, objectFit: 'cover', borderRadius: 8, border: `1.5px solid ${guardada ? 'var(--ok-line)' : 'var(--hair)'}`, background: 'var(--panel)', display: 'block' }} />
      <button type="button" onClick={onQuitar} title={guardada ? 'Eliminar imagen guardada' : 'Quitar imagen'}
        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, lineHeight: '17px', textAlign: 'center', padding: 0, borderRadius: '50%', border: '1px solid var(--danger-line)', background: 'var(--panel)', color: 'var(--danger)', cursor: 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 2px 6px rgba(15,23,42,0.18)' }}>×</button>
      <div title={nombre} style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {guardada && <Icono nombre="check" size={13} style={{ color: 'var(--ok)', verticalAlign: 'middle', marginRight: 3 }} />}{nombre}
      </div>
    </div>
  );
}

export default function NuevaRequisicion({ onCreated }) {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [sucursalesAll, setSucursalesAll] = useState([]);
  const [centros, setCentros] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [toast, setToast] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    empresa: '', sucursal: '', centroCostos: '', direccion: '', departamento: '',
    fechaSolicitud: hoyISO(),
    proveedorSugerido: { nombre: '', rfc: '', contacto: '', telefono: '', correo: '', condicionesPago: '', tiempoEntrega: '', direccion: '' },
    justificacion: '',
  });
  const [partidas, setPartidas] = useState([partidaVacia()]);
  // Modo edición: viene de "Corregir y reenviar" (rechazada) o de "Editar" en el
  // historial, disponible mientras el comparativo no haya arrancado.
  const [corrigiendo, setCorrigiendo] = useState(null);
  // Imágenes de la justificación. Por seleccionar (aún en el navegador) vs. las
  // que ya viven en el servidor — estas últimas solo aparecen al corregir.
  const [imagenes, setImagenes] = useState([]);           // [{ file, url }]
  const [imagenesSubidas, setImagenesSubidas] = useState([]);
  // Si la requisición se creó pero la subida falló, se guarda a dónde reintentar
  // para no perder los archivos que ya eligió el usuario.
  const [reintentoImgs, setReintentoImgs] = useState(null); // { id, folio }
  const fileRef = useRef(null);

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4500); };

  useEffect(() => {
    try {
      const raw = localStorage.getItem('requisicionCorregir');
      if (!raw) return;
      const r = JSON.parse(raw);
      setCorrigiendo({ id: r._id, folio: r.folio, motivoRechazo: [r.motivoRechazoCategoria, r.motivoRechazo].filter(Boolean).join(' — '), estado: r.estado || 'rechazada' });
      setForm({
        empresa: r.empresa || '', sucursal: r.sucursal || '', centroCostos: r.centroCostos || '',
        direccion: r.direccion || '', departamento: r.departamento || '',
        fechaSolicitud: r.fechaSolicitud || hoyISO(),
        proveedorSugerido: { nombre: '', rfc: '', contacto: '', telefono: '', correo: '', condicionesPago: '', tiempoEntrega: '', direccion: '', ...(r.proveedorSugerido || {}) },
        justificacion: r.justificacion || '',
      });
      // Requisiciones capturadas antes de que la tasa viviera en la partida: el IVA
      // era un checkbox de todo o nada, así que un `iva` en cero era la intención
      // explícita de no gravar. Sin esto, al corregirlas les reaparecería el 16%.
      const sinIvaHistorico = (r.iva || 0) === 0 && (r.subtotal || 0) > 0;
      const tasaHeredada = (p) => (p.tasaIva == null && sinIvaHistorico ? 0 : tasaDe(p));
      setPartidas((r.partidas || []).length
        ? r.partidas.map(p => ({ material: p.material || '', descripcion: p.descripcion || '', unidad: p.unidad || '', cantidad: String(p.cantidad ?? ''), precioUnitario: String(p.precioUnitario ?? ''), tasaIva: tasaHeredada(p) }))
        : [partidaVacia()]);
      setImagenesSubidas(r.justificacionImagenes || []);
    } catch {}
  }, []);

  // Los blobs de las miniaturas se liberan al salir de la pantalla. Va por ref
  // para no revocar en cada cambio de la lista una URL que aún se está mostrando.
  const imagenesRef = useRef(imagenes);
  imagenesRef.current = imagenes;
  useEffect(() => () => imagenesRef.current.forEach(i => URL.revokeObjectURL(i.url)), []);

  const limpiarImagenes = () => {
    imagenes.forEach(i => URL.revokeObjectURL(i.url));
    setImagenes([]);
    setImagenesSubidas([]);
    setReintentoImgs(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const agregarImagenes = (files) => {
    const elegidas = Array.from(files || []);
    if (fileRef.current) fileRef.current.value = ''; // permite volver a elegir el mismo archivo
    const validas = [];
    for (const f of elegidas) {
      if (!IMAGEN_RE.test(f.name)) { toast_(`${f.name}: solo se aceptan imágenes JPG, PNG o WEBP`, 'error'); continue; }
      if (f.size > MAX_IMAGEN_MB * 1024 * 1024) { toast_(`${f.name} pesa más de ${MAX_IMAGEN_MB} MB`, 'error'); continue; }
      validas.push({ file: f, url: URL.createObjectURL(f) });
    }
    if (!validas.length) return;
    const cupo = Math.max(0, MAX_IMAGENES - imagenes.length - imagenesSubidas.length);
    if (validas.length > cupo) {
      toast_(`Puedes adjuntar hasta ${MAX_IMAGENES} imágenes en total`, 'error');
      validas.slice(cupo).forEach(v => URL.revokeObjectURL(v.url));
    }
    if (cupo) setImagenes(prev => [...prev, ...validas.slice(0, cupo)]);
  };

  const quitarImagen = (i) => setImagenes(prev => {
    if (prev[i]) URL.revokeObjectURL(prev[i].url);
    return prev.filter((_, idx) => idx !== i);
  });

  // Borra del servidor una imagen ya guardada (solo en modo corrección)
  const borrarImagenSubida = async (img) => {
    if (!corrigiendo) return;
    try {
      const r = await api.del(`/requisiciones/${corrigiendo.id}/justificacion-imagenes/${img._id}`);
      setImagenesSubidas(r.justificacionImagenes || []);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // La evidencia se sube aparte: el POST que crea la requisición es JSON y no
  // admite archivos, así que primero nace el folio y luego se le cuelgan.
  const subirImagenes = async (reqId) => {
    if (!imagenes.length) return;
    const fd = new FormData();
    imagenes.forEach(v => fd.append('imagenes', v.file));
    await api.upload(`/requisiciones/${reqId}/justificacion-imagenes`, fd);
  };

  const reintentarImagenes = async () => {
    if (!reintentoImgs) return;
    try {
      await subirImagenes(reintentoImgs.id);
      toast_(`Imágenes adjuntadas a la requisición ${reintentoImgs.folio}.`);
      limpiarImagenes();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const cancelarCorreccion = () => {
    localStorage.removeItem('requisicionCorregir');
    setCorrigiendo(null);
    setForm(f => ({ ...f, departamento: '', justificacion: '' }));
    setPartidas([partidaVacia()]);
    limpiarImagenes();
  };

  useEffect(() => {
    api.get('/empresas').then(all => {
      if (user?.rol === 'solicitante' && user?.empresas?.length) {
        const propias = all.filter(e => user.empresas.includes(e.nombre));
        setEmpresas(propias);
        if (propias.length === 1) setForm(f => ({ ...f, empresa: propias[0].nombre }));
      } else setEmpresas(all);
    }).catch(() => {});
    api.get('/sucursales').then(setSucursalesAll).catch(() => {});
    api.get('/materiales').then(setMateriales).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!form.sucursal) { setCentros([]); return; }
    api.get(`/centros?sucursal=${encodeURIComponent(form.sucursal)}`)
      .then(list => setCentros(
        user?.rol === 'solicitante' && user?.centrosCostos?.length
          ? list.filter(c => user.centrosCostos.includes(c.nombre))
          : list
      ))
      .catch(() => setCentros([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sucursal]);

  const empresaObj = empresas.find(e => e.nombre === form.empresa);
  const sucursalesBase = empresaObj?.sucursales?.length
    ? sucursalesAll.filter(s => empresaObj.sucursales.includes(s.nombre))
    : sucursalesAll;
  const sucursalesFiltradas = (user?.rol === 'solicitante' && user?.sucursales?.length)
    ? sucursalesBase.filter(s => user.sucursales.includes(s.nombre))
    : sucursalesBase;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };
  const setProv = (k, v) => setForm(f => ({ ...f, proveedorSugerido: { ...f.proveedorSugerido, [k]: v } }));

  const setPartida = (i, k, v) => setPartidas(ps => ps.map((p, idx) => idx !== i ? p : { ...p, [k]: v }));
  const elegirMaterial = (i, m) => setPartidas(ps => ps.map((p, idx) => idx !== i ? p
    : { ...p, material: m._id, descripcion: m.nombre,
        // La unidad es opcional en el catálogo: si el material no la trae, se
        // respeta la que el usuario escribió a mano. Antes se pisaba con '' y
        // la partida terminaba sin unidad en el PDF y en la orden de compra.
        unidad: (m.unidad || '').trim() || p.unidad,
        // prellena el precio con el último costo conocido del catálogo (editable)
        precioUnitario: (m.costoUnitario !== null && m.costoUnitario !== undefined && p.precioUnitario === '') ? String(m.costoUnitario) : p.precioUnitario,
        // la tasa del producto manda sobre la que hubiera en el renglón (sigue siendo editable)
        tasaIva: tasaDe(m) }));
  // Al volver a captura libre se limpia la descripción (era el nombre del
  // catálogo) pero la unidad se conserva: casi siempre sigue siendo la correcta.
  const quitarMaterial = (i) => setPartidas(ps => ps.map((p, idx) => idx !== i ? p
    : { ...p, material: '', descripcion: '' }));
  const importe = (p) => (parseFloat(p.cantidad) || 0) * (parseFloat(p.precioUnitario) || 0);
  const ivaDe = (p) => importe(p) * (tasaDe(p) / 100);
  const subtotal = partidas.reduce((s, p) => s + importe(p), 0);
  const iva = partidas.reduce((s, p) => s + ivaDe(p), 0);
  const total = subtotal + iva;
  // Para explicar el IVA cuando la requisición mezcla tasas
  const tasasUsadas = [...new Set(partidas.filter(p => importe(p) > 0).map(tasaDe))].sort((a, b) => b - a);
  const fmt = (n) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  const enviar = async () => {
    const errs = {};
    if (!form.empresa) errs.empresa = true;
    if (!form.sucursal) errs.sucursal = true;
    if (!form.centroCostos) errs.centroCostos = true;
    const partidasValidas = partidas.filter(p => p.descripcion.trim() && parseFloat(p.cantidad) > 0);
    if (!partidasValidas.length) errs.partidas = true;
    setErrors(errs);
    if (Object.keys(errs).length) { toast_('Completa los campos obligatorios y al menos una partida', 'error'); return; }
    setEnviando(true);
    try {
      const payload = {
        ...form,
        partidas: partidasValidas.map(p => ({
          material: p.material || undefined,
          descripcion: p.descripcion.trim(), unidad: p.unidad,
          cantidad: parseFloat(p.cantidad) || 0,
          precioUnitario: parseFloat(p.precioUnitario) || 0,
          importe: importe(p),
          tasaIva: tasaDe(p),
        })),
        // El backend recalcula los totales a partir de las partidas; van para el previo.
        subtotal, iva, total, moneda: 'MXN',
      };
      let reqId, folio, mensaje;
      // A quién le toca primero: el área (si hay regla) o el jefe de compras
      const siguiente = (doc) => doc?.estado === 'en_validacion'
        ? `Queda en aprobación de tu área (${(doc.aprobacionArea?.pasos?.[0]?.aprobadores || []).map(a => a.nombre).join(' o ')}); se le avisó por correo.`
        : 'Queda pendiente de aprobación del Jefe de Compras.';
      if (corrigiendo) {
        const guardada = await api.post(`/requisiciones/${corrigiendo.id}/reenviar`, payload);
        reqId = corrigiendo.id; folio = corrigiendo.folio;
        mensaje = corrigiendo.estado === 'rechazada'
          ? `Requisición ${folio} corregida y reenviada. ${siguiente(guardada)}`
          : guardada?.reabreAprobacion
            ? `Requisición ${folio} actualizada. Como ya estaba aprobada, vuelve a empezar su aprobación. ${siguiente(guardada)}`
            : `Requisición ${folio} actualizada. ${siguiente(guardada)}`;
        localStorage.removeItem('requisicionCorregir');
        setCorrigiendo(null);
      } else {
        const creada = await api.post('/requisiciones', payload);
        reqId = creada._id; folio = creada.folio;
        mensaje = `Requisición ${folio} creada. ${siguiente(creada)}`;
      }
      // La requisición ya existe: si la evidencia falla, se avisa y se conserva
      // lo elegido para reintentar, en vez de dar por adjuntado lo que no llegó.
      let imagenesOk = true;
      try {
        await subirImagenes(reqId);
        limpiarImagenes();
        toast_(mensaje);
      } catch (e) {
        imagenesOk = false;
        setReintentoImgs({ id: reqId, folio });
        toast_(`${mensaje} PERO las imágenes no se adjuntaron: ${e.message}`, 'error');
      }
      setPartidas([partidaVacia()]);
      setForm(f => ({ ...f, departamento: '', justificacion: '', proveedorSugerido: { nombre: '', rfc: '', contacto: '', telefono: '', correo: '', condicionesPago: '', tiempoEntrega: '', direccion: '' } }));
      // Si quedó pendiente el reintento no se navega: el aviso vive en esta pantalla
      if (onCreated && imagenesOk) setTimeout(onCreated, 800);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setEnviando(false); }
  };

  const thStyle = { padding: '7px 8px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' };
  // Corregir un rechazo y editar una que sigue en fila usan el mismo formulario,
  // pero el aviso y los botones cambian de tono.
  const esRechazada = corrigiendo?.estado === 'rechazada';

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: 980 }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader icon="carrito" title={corrigiendo ? `${esRechazada ? 'Corregir' : 'Editar'} Requisición ${corrigiendo.folio}` : 'Nueva Requisición de Compra'}
        subtitle="Solicita materiales o servicios; la aprueban tu área (si tiene aprobadores) y el Jefe de Compras, y el Comprador la cotiza" />

      {/* Aviso de modo edición — rojo al corregir un rechazo, ámbar al solo editar */}
      {corrigiendo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 16px', borderRadius: 10,
          background: esRechazada ? 'var(--danger-bg)' : 'var(--warn-bg)',
          border: `1px solid ${esRechazada ? 'var(--danger-line)' : 'var(--warn-line)'}` }}>
          <span style={{ display: 'inline-flex', color: esRechazada ? 'var(--danger)' : 'var(--warn)' }}><Icono nombre={esRechazada ? 'refrescar' : 'editar'} size={18} /></span>
          <div style={{ flex: 1, fontSize: 12.5, color: esRechazada ? 'var(--danger)' : 'var(--warn)' }}>
            {esRechazada ? <>
              Estás corrigiendo la requisición <b>{corrigiendo.folio}</b> (rechazada).
              {corrigiendo.motivoRechazo && <> Motivo del rechazo: <b>{corrigiendo.motivoRechazo}</b>.</>}
              {' '}Al enviarla volverá a quedar pendiente de aprobación.
            </> : <>
              Estás editando la requisición <b>{corrigiendo.folio}</b>. Se puede porque el comprador
              aún no arma el cuadro comparativo.
              {corrigiendo.estado === 'aprobada'
                ? <> Ojo: ya está <b>aprobada</b>, así que al guardar volverá a <b>pendiente</b> y el Jefe de Compras tendrá que aprobarla de nuevo.</>
                : <> Al guardar sigue pendiente de aprobación.</>}
            </>}
          </div>
          <button onClick={cancelarCorreccion} title="Salir del modo edición"
            style={{ ...css.btn('secundario'), padding: '4px 10px', fontSize: 11.5 }}>
            Cancelar
          </button>
        </div>
      )}

      <div style={{ ...css.card, padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <SectionHeader>Datos del Solicitante</SectionHeader>
        <Grid cols={3} gap={12}>
          <div><Label required>Compañía</Label>
            <select value={form.empresa} onChange={e => { set('empresa', e.target.value); set('sucursal', ''); set('centroCostos', ''); }} style={css.select(errors.empresa)}>
              <option value="">— Selecciona —</option>
              {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
            </select></div>
          <div><Label required>Sucursal</Label>
            <select value={form.sucursal} onChange={e => { set('sucursal', e.target.value); set('centroCostos', ''); }} style={css.select(errors.sucursal)}>
              <option value="">— Selecciona —</option>
              {sucursalesFiltradas.map(s => <option key={s._id || s.nombre} value={s.nombre}>{s.nombre}</option>)}
            </select></div>
          <div><Label required>Centro de Costos</Label>
            <select value={form.centroCostos} onChange={e => {
              const c = centros.find(cc => cc.nombre === e.target.value);
              setForm(f => ({ ...f, centroCostos: e.target.value, direccion: c?.seccion?.trim() || c?.direccion?.trim() || f.direccion }));
              if (errors.centroCostos) setErrors(er => ({ ...er, centroCostos: false }));
            }} style={css.select(errors.centroCostos)}>
              <option value="">— Selecciona —</option>
              {centros.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>)}
            </select></div>
          <div><Label>Departamento / Área solicitante</Label>
            <input value={form.departamento} onChange={e => set('departamento', e.target.value)} placeholder="Ej: Sistemas, Mantenimiento..." style={css.input(false)} /></div>
          <div><Label>Fecha de solicitud</Label>
            <input type="date" value={form.fechaSolicitud} onChange={e => set('fechaSolicitud', e.target.value)} style={css.input(false)} /></div>
        </Grid>

        <SectionHeader>Datos del Proveedor (sugerido, opcional)</SectionHeader>
        <Grid cols={3} gap={12}>
          <div><Label>Nombre del proveedor</Label><input value={form.proveedorSugerido.nombre} onChange={e => setProv('nombre', e.target.value)} style={css.input(false)} /></div>
          <div><Label>RFC</Label><input value={form.proveedorSugerido.rfc} onChange={e => setProv('rfc', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Contacto</Label><input value={form.proveedorSugerido.contacto} onChange={e => setProv('contacto', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Teléfono</Label><input value={form.proveedorSugerido.telefono} onChange={e => setProv('telefono', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Correo</Label><input value={form.proveedorSugerido.correo} onChange={e => setProv('correo', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Condiciones de pago</Label><input value={form.proveedorSugerido.condicionesPago} onChange={e => setProv('condicionesPago', e.target.value)} style={css.input(false)} /></div>
          <div><Label>Tiempo de entrega estimado</Label><input value={form.proveedorSugerido.tiempoEntrega} onChange={e => setProv('tiempoEntrega', e.target.value)} style={css.input(false)} /></div>
          <div style={{ gridColumn: 'span 2' }}><Label>Dirección</Label><input value={form.proveedorSugerido.direccion} onChange={e => setProv('direccion', e.target.value)} style={css.input(false)} /></div>
        </Grid>

        <SectionHeader>Partidas</SectionHeader>
        {errors.partidas && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Agrega al menos una partida con descripción y cantidad.</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
              <th style={{ ...thStyle, width: 220 }}>Material (catálogo)</th>
              <th style={thStyle}>Descripción</th>
              <th style={{ ...thStyle, width: 70 }}>Unidad</th>
              <th style={{ ...thStyle, width: 80 }}>Cantidad</th>
              <th style={{ ...thStyle, width: 110 }}>P. Unitario</th>
              <th style={{ ...thStyle, width: 78 }}>IVA</th>
              <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>Subtotal</th>
              <th style={{ ...thStyle, width: 40 }}></th>
            </tr></thead>
            <tbody>
              {partidas.map((p, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--hair-soft)' }}>
                  <td style={{ padding: 6 }}>
                    <MaterialPicker materiales={materiales} partida={p}
                      onPick={m => elegirMaterial(i, m)} onClear={() => quitarMaterial(i)} />
                  </td>
                  <td style={{ padding: 6 }}><input value={p.descripcion} onChange={e => setPartida(i, 'descripcion', e.target.value)} placeholder="Producto o servicio" style={{ ...css.input(false), fontSize: 12 }} /></td>
                  <td style={{ padding: 6 }}><input value={p.unidad} onChange={e => setPartida(i, 'unidad', e.target.value)} placeholder="pza" style={{ ...css.input(false), fontSize: 12 }} /></td>
                  <td style={{ padding: 6 }}><input type="number" min="0" value={p.cantidad} onChange={e => setPartida(i, 'cantidad', e.target.value)} style={{ ...css.input(false), fontSize: 12 }} /></td>
                  <td style={{ padding: 6 }}><MontoInput value={p.precioUnitario} onChange={v => setPartida(i, 'precioUnitario', v)} style={{ fontSize: 12 }} /></td>
                  <td style={{ padding: 6 }}>
                    <select value={tasaDe(p)} onChange={e => setPartida(i, 'tasaIva', Number(e.target.value))}
                      title="Tasa de IVA de este producto (viene del catálogo)"
                      style={{ ...css.select(false), fontSize: 12, padding: '6px 4px' }}>
                      {TASAS_IVA.map(t => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', fontFamily: 'var(--digit-font)', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmt(importe(p))}</td>
                  <td style={{ padding: 6 }}>
                    {partidas.length > 1 && (
                      <button onClick={() => setPartidas(ps => ps.filter((_, idx) => idx !== i))} title="Quitar partida"
                        aria-label="Quitar partida" style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontSize: 12 }}><Icono nombre="cerrar" size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <button onClick={() => setPartidas(ps => [...ps, partidaVacia()])}
            style={{ ...css.btn('secundario'), fontSize: 12.5 }}><ConIcono nombre="mas">Agregar partida</ConIcono></button>
        </div>

        {/* Totales */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 300, background: 'var(--panel)', borderRadius: 10, border: '1px solid var(--hair)', padding: '12px 16px', fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: 'var(--glass)' }}>SUB TOTAL</span><span style={{ fontFamily: 'var(--digit-font)', fontWeight: 600 }}>{fmt(subtotal)}</span></div>
            {/* El IVA se suma renglón por renglón con la tasa de cada partida:
                no hay una tasa única que mostrar cuando la compra mezcla. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', alignItems: 'center' }}>
              <span style={{ color: 'var(--glass)' }}>
                IVA{tasasUsadas.length === 1 ? ` (${tasasUsadas[0]}%)` : tasasUsadas.length > 1 ? ` (${tasasUsadas.map(t => `${t}%`).join(' + ')})` : ''}
              </span>
              <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 600 }}>{fmt(iva)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', borderTop: '1px solid var(--hair)', marginTop: 4 }}>
              <span style={{ fontWeight: 700, color: 'var(--ink)' }}>TOTAL</span>
              <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 800, color: 'var(--plasma)' }}>{fmt(total)} MXN</span>
            </div>
          </div>
        </div>

        <div><Label>Justificación</Label>
          <textarea value={form.justificacion} onChange={e => set('justificacion', e.target.value)} rows={3}
            placeholder="¿Para qué se necesita esta compra?" style={{ ...css.input(false), resize: 'vertical' }} />

          {/* Evidencia: fotos que respaldan la justificación (el equipo dañado,
              el material que se pide, una cotización en papel...). Aparecen en
              el detalle del historial y en el PDF que firma el Jefe de Compras. */}
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
                onChange={e => agregarImagenes(e.target.files)} style={{ display: 'none' }} />
              <button type="button" onClick={() => fileRef.current?.click()}
                disabled={imagenes.length + imagenesSubidas.length >= MAX_IMAGENES}
                style={{ ...css.btn('secundario'), fontSize: 12.5, opacity: imagenes.length + imagenesSubidas.length >= MAX_IMAGENES ? 0.5 : 1 }}>
                <ConIcono nombre="imagen">Agregar imagen</ConIcono>
              </button>
              <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>
                Opcional · JPG, PNG o WEBP · hasta {MAX_IMAGEN_MB} MB cada una · máximo {MAX_IMAGENES}
              </span>
            </div>

            {(imagenes.length > 0 || imagenesSubidas.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                {imagenesSubidas.map(img => (
                  <Miniatura key={img._id} src={`${BASE}/requisiciones/${corrigiendo?.id}/justificacion-imagenes/${img._id}/preview?token=${localStorage.getItem('token')}`}
                    nombre={img.nombreOriginal} guardada onQuitar={() => borrarImagenSubida(img)} />
                ))}
                {imagenes.map((v, i) => (
                  <Miniatura key={i} src={v.url} nombre={v.file.name} onQuitar={() => quitarImagen(i)} />
                ))}
              </div>
            )}

            {reintentoImgs && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, padding: '9px 12px', borderRadius: 8, background: 'var(--danger-bg)', border: '1px solid var(--danger-line)' }}>
                <span style={{ flex: 1, fontSize: 12, color: 'var(--danger)' }}>
                  La requisición <b>{reintentoImgs.folio}</b> se guardó, pero estas imágenes no se adjuntaron.
                </span>
                <button type="button" onClick={reintentarImagenes} style={{ ...css.btn('secundario'), fontSize: 12 }}>Reintentar subida</button>
                <button type="button" onClick={limpiarImagenes}
                  style={{ ...css.btn('peligro', true), padding: '5px 10px', fontSize: 11.5 }}>Descartar</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={enviar} disabled={enviando} style={{ ...css.btn('primario'), padding: '11px 26px', opacity: enviando ? 0.6 : 1 }}>
            {enviando ? 'Enviando…' : !corrigiendo ? <ConIcono nombre="enviar">Crear Requisición</ConIcono> : esRechazada ? <ConIcono nombre="refrescar">Reenviar Requisición corregida</ConIcono> : <ConIcono nombre="guardar">Guardar cambios</ConIcono>}
          </button>
        </div>
      </div>
    </div>
  );
}
